import { formatLocalizedDate, translate, translateChoice } from '@/i18n'

export const NOT_SPECIFIED = '__not_specified__'

function notSpecified() { return translate('common.notSpecified') }

function firstDefined(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '')
}

function displayValue(value) {
  if (value === undefined || value === null || value === '') return notSpecified()
  if (typeof value === 'object') {
    return firstDefined(value.name, value.title, value.label, value.value, notSpecified())
  }
  return String(value)
}

function displayChoice(group, value) {
  if (!value) return notSpecified()
  return translateChoice(group, value, displayValue(value))
}

function formatDate(value) {
  if (!value) return notSpecified()
  return formatLocalizedDate(value, { day: '2-digit', month: 'short', year: 'numeric' })
}

function availability(value) {
  if (value === undefined || value === null || value === '') return notSpecified()
  return value === false ? translate('common.notAvailable') : translate('common.available')
}

function maskAccount(value) {
  if (!value) return notSpecified()
  const account = String(value)
  if (account.includes('*')) return account
  const visiblePart = account.replace(/\s/g, '').slice(-4)
  return visiblePart ? `****${visiblePart}` : notSpecified()
}

export function normalizeEmployee(payload = {}) {
  const employee = payload.employee || payload
  const identity = payload.employee ? payload : employee
  const fullName = firstDefined(
    employee.display_name,
    identity.display_name,
    [identity.first_name, identity.last_name].filter(Boolean).join(' '),
    employee.full_name,
    identity.full_name,
    notSpecified()
  )
  const position = displayValue(firstDefined(employee.position, employee.job_title, employee.position_title))
  const department = displayValue(firstDefined(employee.department, employee.department_name))

  return {
    fullName,
    firstName: identity.first_name,
    lastName: identity.last_name,
    photoUrl: identity.crop?.crop_url,
    position,
    department,
    employmentStatus: displayChoice('employmentStatuses', firstDefined(
      employee.employment_status,
      payload.employment?.status,
      payload.employment_status
    )),
  }
}

export function normalizeProfile(payload = {}) {
  const source = payload.profile || payload
  const contacts = source.contacts || {}
  const salary = source.payment || {}
  const contract = source.current_contract
  const contractPeriod = contract ? `${formatDate(contract.from_date)} – ${formatDate(contract.to_date)}` : notSpecified()

  return {
    employee: normalizeEmployee(source),
    employment: [
      { label: translate('common.status'), value: displayChoice('employmentStatuses', source.employment_status) },
      { label: translate('profile.type'), value: displayChoice('employmentTypes', source.employment_type) },
      { label: translate('profile.employedSince'), value: formatDate(source.date_of_employment) },
      { label: translate('profile.legalEntity'), value: displayValue(contract?.legal_entity) },
      { label: translate('profile.contractPeriod'), value: contractPeriod },
    ],
    contacts: [
      { label: translate('profile.email'), value: displayValue(firstDefined(contacts.email, source.email)) },
      { label: translate('profile.phone'), value: displayValue(firstDefined(contacts.phone, source.phone)) },
      { label: translate('profile.whatsapp'), value: availability(contacts.phone_whatsapp) },
      { label: translate('profile.telegram'), value: availability(contacts.phone_telegram) },
    ],
    salary: [
      { label: translate('profile.method'), value: displayChoice('paymentMethods', firstDefined(salary.method, salary.payment_method)) },
      { label: translate('profile.bank'), value: displayValue(firstDefined(salary.bank, salary.bank_name)) },
      { label: translate('profile.account'), value: maskAccount(salary.bank_account_masked) },
    ],
  }
}

export function employeeSubtitle(employee) {
  return [employee.position, employee.department]
    .filter(value => value && value !== notSpecified())
    .join(' · ') || notSpecified()
}
